import { clsx } from 'clsx'
import { forwardRef } from 'react'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

const Input = forwardRef(({ 
  label, 
  error, 
  hint,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className = '',
  ...props 
}, ref) => {
  const { isMobileUI, isAdminAppUI } = useLayoutVariant()
  
  return (
    <div className="w-full">
      {label && (
        <label className={clsx(
          'block text-sm font-medium mb-1.5',
          isAdminAppUI ? 'text-[#7a8fa6]' : isMobileUI ? 'text-pastel-ink' : 'text-pastel-ink dark:text-gray-300'
        )}>
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {LeftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LeftIcon className={clsx(
              'h-5 w-5',
              isAdminAppUI ? 'text-[#3d5166]' : isMobileUI ? 'text-pastel-ink/60' : 'text-pastel-ink/60 dark:text-gray-500'
            )} />
          </div>
        )}
        <input
          ref={ref}
          className={clsx(
            'block w-full transition-all duration-300',
            isAdminAppUI
              ? 'rounded-[10px] border border-[#243246] bg-[#1f2d40] px-3 py-2.5 text-sm text-[#e8edf5] placeholder-[#3d5166] focus:outline-none focus:ring-2 focus:ring-[#00c896]/25 focus:border-[#00c896]'
              : isMobileUI 
              ? 'rounded-2xl border border-pastel-pink bg-white pl-10 pr-4 py-3 text-pastel-ink dark:text-gray-100 placeholder-pastel-ink/50 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pastel-teal/40'
              : 'rounded-2xl border px-3 py-2.5 text-sm',
            LeftIcon && (isMobileUI ? 'pl-10' : 'pl-10'),
            RightIcon && (isMobileUI ? 'pr-10' : 'pr-10'),
            !isMobileUI && !isAdminAppUI && (
              error
                ? 'border-red-300 dark:border-red-600 text-red-900 dark:text-red-200 placeholder-red-300 dark:placeholder-red-700 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500 focus:shadow-lg focus:shadow-red-500/20'
                : 'border-pastel-pink dark:border-gray-600 text-pastel-ink dark:text-gray-100 placeholder-pastel-ink/50 dark:placeholder-gray-500 bg-white dark:bg-gray-800 focus:border-pastel-teal dark:focus:border-primary-400 focus:ring-pastel-teal/40 dark:focus:ring-primary-400 focus:shadow-sm hover:border-pastel-lavender dark:hover:border-gray-500'
            ),
            !isMobileUI && !isAdminAppUI && 'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed',
            error && isMobileUI && 'ring-2 ring-red-500/50',
            error && isAdminAppUI && 'border-[#ef5350] focus:ring-[#ef5350]/25 focus:border-[#ef5350]',
            className
          )}
          {...props}
        />
        {RightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <RightIcon className="h-5 w-5 text-gray-500 dark:text-gray-500" />
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-700 dark:text-red-400">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-500">{hint}</p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

export default Input

